/**
 * Base Regex Validator Implementation
 *
 * Abstract base class for creating strongly-typed regex validators.
 */

import type {
  RegexValidationResult,
  RegexValidator,
  RegexValidatorOptions
} from "./regex-validator.types";

/**
 * Base implementation of RegexValidator
 */
export abstract class BaseRegexValidator<T = string> implements RegexValidator<T> {
  readonly pattern: RegExp;
  readonly description: string;
  readonly examples: readonly string[];
  protected readonly errorMessage: string;
  protected readonly generator?: () => T;
  protected readonly transform: (value: string) => T;

  constructor(options: RegexValidatorOptions<T>) {
    this.pattern = options.pattern;
    this.description = options.description;
    this.examples = options.examples;
    this.errorMessage = options.errorMessage || `Invalid format. Expected: ${options.description}`;
    this.generator = options.generator;
    this.transform = options.transform || ((value) => value as T);
  }

  /**
   * Test if a value matches the regex
   */
  test(value: string): boolean {
    return this.pattern.test(value);
  }

  /**
   * Validate and return typed result
   */
  validate(value: string): RegexValidationResult<T> {
    if (!this.test(value)) {
      return {
        valid: false,
        error: this.errorMessage
      };
    }

    return {
      valid: true,
      value: this.transform(value)
    };
  }

  /**
   * Parse or throw error
   */
  parse(value: string): T {
    const result = this.validate(value);

    if (!result.valid) {
      throw new Error(result.error);
    }

    return result.value;
  }

  /**
   * Generate a valid value (if generator is available)
   */
  generate(): T {
    if (!this.generator) {
      throw new Error(`Generator not available for ${this.description}`);
    }

    return this.generator();
  }
}
